import { BLANK, MAX_GAMEWEEK } from './constants';
import { isEven } from './utils';

const DUMMY_FIXTURES = false;

const playerActionGroup = {
  MY_TEAM: 'MY_TEAM',
  TRANSFER_IN: 'TRANSFER_IN',
  INFO: 'INFO',
  OPTIMISE: 'OPTIMISE'
};

const PLAYER_STATES = {
  default: 'DEFAULT',
  selected: 'SELECTED',
  canSub: 'CAN_SUB',
  cannotSelect: 'CANNOT_SELECT',
  canTransfer: 'CAN_TRANSFER',
  cannotTransfer: 'CANNOT_TRANSFER',
  canAddToTeam: 'CAN_ADD_TO_TEAM',
  none: 'NONE',
  ALWAYS_INCLUDE: 'ALWAYS_INCLUDE',
  ALWAYS_EXCLUDE: 'ALWAYS_EXCLUDE'
};

const PLAYER_POSITIONS = {
  1: { short: 'GKP', full: 'Goalkeeper' },
  2: { short: 'DEF', full: 'Defender' },
  3: { short: 'MID', full: 'Midfielder' },
  4: { short: 'FWD', full: 'Forward' }
};

const getPositionIdFromPositionLabel = (
  positionLabel: string
): number | undefined => {
  if (!positionLabel) {
    return undefined;
  }
  const label = positionLabel.toUpperCase();

  for (const [id, position] of Object.entries(PLAYER_POSITIONS)) {
    if (
      position.short.toUpperCase() === label ||
      position.full.toUpperCase() === label
    ) {
      return Number(id);
    }
  }

  return undefined;
};

const DEFAULT_PLAYER_STATUS = 'a'; // FPL's status code for available

export type PlayerType = {
  code: number;
  fpl: FPLData;
  data: PlayerData;
  searchTerm?: string;
  paidPrice?: number;
  position: number;
  customData?: any;
  multiplier: number;
  is_captain?: boolean;
  is_vice_captain?: boolean;
  squadIndex?: number;
  webName?: string;
  team: TeamType;
  live?: any;
  replacingId?: number;
  replacedById?: number;
};

type TeamType = {
  code: number;
  name: string;
  codeName: string;
};

type FPLData = {
  id: number;
  web_name: string;
  now_cost: number;
  cost_change_start: number;
  status?: string;
  element_type?: number;
  team?: number;
  selected_by_percent?: string;
  chance_of_playing_this_round?: number | null;
  chance_of_playing_next_round?: number | null;
};

type PlayerData = {
  predictions: any[];
  predicted_fixture_predictions?: any[];
  teamCodeName: string;
  positionId: number;
  weighted_prediction?: number;
  status: string;
  priceInfo: any;
  prediction4GW: number;
  nowCost: number;
  formatted_cost?: string;
};

type TransferHistory = {
  element_in_cost: number;
  element_in: number;
};

export type Prediction = {
  gw: number;
  opp: (string | number)[][];
  capt: number;
  xmins: number;
  status: string;
  fitness: number;
  predicted_pts: number;
};

const addDataToPlayerArray = (players: PlayerType[], data: PlayerType[]) => {
  const newPlayers: PlayerType[] = [];
  players?.forEach((p) => {
    if (p.code) {
      const pData = findPlayerByCode(data, p.code);
      const newPlayer = addDataToPlayer(p, pData);
      newPlayers.push(newPlayer);
    } else {
      newPlayers.push(p);
    }
  });
  return newPlayers;
};

const addPurchasePriceToPlayerArray = (
  players: PlayerType[],
  transferHistory: TransferHistory[]
) => {
  return players.map((player) => {
    // Preserve any paidPrice already on the player. transferredPlayers sets it
    // to the price actually paid, which the FPL transfer history will not yet
    // reflect for an unsaved transfer. Recomputing here would clobber that to
    // the season-start price and underprice the next sale.
    if (typeof player.paidPrice === 'number') {
      return player;
    }
    return addDataToPlayer(player, {
      paidPrice: playerPaidPrice(player, transferHistory)
    });
  });
};

const addDataToPlayer = (player: PlayerType, data: any) => {
  const updatedPlayer = { ...player };
  Object.keys(data).forEach((key) => {
    updatedPlayer[key] = data[key];
  });

  return updatedPlayer;
};

const playerPaidPrice = (
  player: PlayerType,
  transferHistory: TransferHistory[]
) => {
  if (isBlankPlayer(player)) {
    return 0;
  }

  const latestPurchase = [...transferHistory].find(
    (p) => p.element_in === player.fpl.id
  );

  if (latestPurchase) {
    return latestPurchase.element_in_cost;
  } else {
    return playerStartCost(player);
  }
};

const isBlankPlayer = (player: PlayerType) => {
  return playerTeamCode(player) === BLANK;
};

const findPlayerByCode = (playerData: PlayerType[], code: number) => {
  return playerData.find((element) => {
    return element.code === code;
  });
};

const playerPositionId = (player: PlayerType | any) => {
  return player?.data?.positionId;
};

const playerPositionName = (player: PlayerType, type = 'short') => {
  const positionId = playerPositionId(player);
  const positionData = PLAYER_POSITIONS[positionId];

  return positionData[type];
};

const playerTeamCode = (player: PlayerType) => {
  if (!player.data) {
    return null;
  }
  return player.data.teamCodeName;
};

const formatPlayerName = (player: PlayerType, truncate = 11) => {
  let name = player.fpl?.web_name || player.webName;
  if (name && name.length > truncate) {
    name = `${name.slice(0, truncate - 1)}...`;
  }

  return name;
};

const playerName = (player: PlayerType) => {
  return player.fpl.web_name;
};

const playerCost = (player: PlayerType) => {
  return player.fpl.now_cost;
};

const isOutfield = (player: PlayerType) => {
  return typeof player.multiplier === 'number' && player?.multiplier >= 1;
};

const isSub = (player: PlayerType) => {
  return typeof player.multiplier === 'number' && player.multiplier <= 0;
};

const squadRole = (player: PlayerType) => {
  if (player.is_captain) {
    return 'captain';
  }
  if (player.is_vice_captain) {
    return 'vice-captain';
  }

  if (player.multiplier === 1) {
    return 'standard';
  }

  return 'substitute';
};

const playerStartCost = (player: PlayerType) => {
  return player.fpl.now_cost - player.fpl?.cost_change_start;
};

const playerFixtureData = (player: PlayerType) => {
  const prediction = player.data.predictions[0];
  const fixtures = prediction.opp;

  const fixtureData: {
    value: number;
    classes: string;
  }[] = [];
  fixtures.forEach((fixture) => {
    let opponent = fixture[0];
    if (fixture[1].indexOf('(H)') > -1) {
      opponent += ' (H)';
    } else {
      opponent += ' (A)';
    }
    fixtureData.push({
      value: opponent,
      classes: 'bg-ff-green'
    });
  });

  return fixtureData;
};

const gwPrediction = (player: PlayerType) => {
  const prediction = player.data.predictions[0];
  return prediction.predicted_pts;
};

const playerSellPrice = (player: PlayerType) => {
  const nowCost = playerCost(player);

  if (!player.paidPrice || player.paidPrice >= nowCost) {
    return nowCost;
  }

  const priceDifference = nowCost - player.paidPrice;

  let priceGain;
  if (isEven(priceDifference)) {
    priceGain = priceDifference / 2;
  } else {
    priceGain = (priceDifference - 1) / 2;
  }

  return player.paidPrice + priceGain;
};

const queryData = (player, key) => {
  if (!key) {
    return null;
  }
  const keys = key.split('__');
  let data = player;
  keys.forEach((k) => {
    data = data[k];
  });

  return data;
};

const playerPredictions = (player: PlayerType) => {
  return player.data.predictions;
};

const playerWeightedPrediction = (player: PlayerType) => {
  return player.data.weighted_prediction;
};

const playerSearchTerm = (player: PlayerType) => {
  return player.searchTerm;
};

const playerGameweekPrediction = (
  player: PlayerType,
  desiredGameweek: number
) => {
  const predictions = playerPredictions(player);

  if (!predictions) return 0;

  const gameweek = predictions.find((gw) => gw.gw == desiredGameweek);

  const nullPoints = DUMMY_FIXTURES ? randomFloatFromInterval(1, 9) : 0;
  const predictedPoints = gameweek ? gameweek.predicted_pts : nullPoints;

  return predictedPoints;
};

const getPlayerPredictionsRange = (
  player: PlayerType,
  gameweekStart: number,
  gameweekEnd: number,
  usePredicted?: boolean
) => {
  const predictionsArray: Prediction[] = [];

  if (!player?.data?.predictions) {
    return predictionsArray;
  }

  const predictions = usePredicted
    ? player.data.predicted_fixture_predictions
    : player.data.predictions;

  for (let gw = gameweekStart; gw <= gameweekEnd; gw++) {
    const predictionObject = predictions?.find((p) => p.gw === gw);
    if (predictionObject) {
      predictionsArray.push(predictionObject);
    }

    const dummy_teams = [
      'ARS',
      'BRE',
      'CHE',
      'AVL',
      'EVE',
      'MUN',
      'MCI',
      'LIV',
      'LEI',
      'NEW',
      'TOT',
      'WHM',
      'SOU',
      'NOT',
      'WOL',
      'LEE',
      'WHM',
      'BHA',
      'BOU',
      'FUL',
      'CRY'
    ];

    if (DUMMY_FIXTURES) {
      const randomTeam =
        dummy_teams[Math.floor(Math.random() * dummy_teams.length)];
      const rndInt = randomIntFromInterval(1, 5);
      const dummyPrediction = {
        gw: gw,
        opp: [[randomTeam, 'X', rndInt]],
        capt: 1.0,
        xmins: 0.0,
        status: 'a',
        fitness: 1.0,
        predicted_pts: randomFloatFromInterval(2, 10)
      };
      if (gw < MAX_GAMEWEEK) {
        predictionsArray.push(dummyPrediction);
      }
    }
  }

  return predictionsArray;
};

function randomIntFromInterval(min, max) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

function randomFloatFromInterval(min, max) {
  // min and max included
  return Math.random() * (max - min + 1) + min;
}

export {
  playerActionGroup,
  PLAYER_STATES,
  DEFAULT_PLAYER_STATUS,
  addDataToPlayerArray,
  addPurchasePriceToPlayerArray,
  addDataToPlayer, //
  playerPositionId,
  playerPositionName,
  playerTeamCode,
  playerCost,
  playerName,
  formatPlayerName,
  playerFixtureData,
  playerSellPrice,
  isOutfield,
  isSub,
  gwPrediction,
  isBlankPlayer,
  queryData,
  playerPredictions,
  playerSearchTerm,
  getPlayerPredictionsRange,
  playerWeightedPrediction,
  playerGameweekPrediction,
  squadRole,
  getPositionIdFromPositionLabel
};
